import React from "react";
import { Star } from "lucide-react";

/**
 * ITER-246: Legacy "Ratings" blocks on the car ad detail page. Four
 * groups — Energy Economy / Carbon Emissions / Driver Safety /
 * Overall Safety. Each block shows N/10 stars (parsed from
 * `attributes["<X> Rating"]` — e.g. "8 Star" → 8/10) plus an optional
 * average and description line straight from the attributes dict.
 *
 * Ratings can also arrive as a structured `listing.ratings` object (spec
 * §1) — we prefer attributes since they round-trip through the seller-
 * editable post form, and only fall back to the structured mirror when
 * the attributes are missing.
 */
const GROUPS = [
  {
    key: "energy_economy",
    title: "Energy Economy Rating",
    ratingKey: "Energy Economy Rating",
    averageKey: "Energy Economy Average",
    descriptionKey: "Energy Economy Description",
    averageLabel: "Fuel consumption",
    averageUnit: "L/100km",
  },
  {
    key: "carbon_emissions",
    title: "Carbon Emissions Rating",
    ratingKey: "Carbon Emissions Rating",
    averageKey: "Carbon Emissions Average",
    descriptionKey: "Carbon Emissions Description",
    averageLabel: "CO2 emissions",
    averageUnit: "g/km",
  },
  {
    key: "driver_safety",
    title: "Driver Safety Rating",
    ratingKey: "Driver Safety Rating",
    descriptionKey: "Driver Safety Description",
  },
  {
    key: "overall_safety",
    title: "Overall Safety Rating",
    ratingKey: "Overall Safety Rating",
    descriptionKey: "Overall Safety Description",
  },
];

const parseStars = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Math.max(0, Math.min(10, Math.round(v)));
  const m = String(v).match(/(\d+)/);
  if (!m) return 0;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(10, n));
};

function StarRow({ stars }) {
  return (
    <div
      className="flex items-center gap-0.5"
      role="img"
      aria-label={`${stars} out of 10 stars`}
      data-testid="rating-stars"
    >
      {Array.from({ length: 10 }, (_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < stars ? "text-amber-400 fill-amber-400" : "text-slate-200"
          }`}
        />
      ))}
      <span className="ml-2 text-xs font-bold text-slate-600">{stars}/10</span>
    </div>
  );
}

export default function CarRatings({ listing }) {
  const attrs = listing.attributes || {};
  const structured = listing.ratings || null;

  const rows = GROUPS.map((g) => {
    const ratingVal = attrs[g.ratingKey];
    const structuredEntry = structured && structured[g.key];
    let stars = parseStars(ratingVal);
    if (!stars && structuredEntry?.stars != null) {
      stars = parseStars(structuredEntry.stars);
    }
    const average =
      (g.averageKey && attrs[g.averageKey]) ??
      (structuredEntry?.average ?? "");
    const description =
      (g.descriptionKey && attrs[g.descriptionKey]) ??
      (structuredEntry?.description ?? "");
    return { ...g, stars, average, description };
  }).filter((r) => r.stars > 0 || r.average || r.description);

  if (!rows.length) return null;

  return (
    <div
      data-testid="ad-car-ratings"
      className="mt-4 rounded-2xl bg-white border border-soft p-5"
    >
      <h2 className="font-display text-lg font-bold text-slate-800">Ratings</h2>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((r) => (
          <div
            key={r.key}
            data-testid={`rating-block-${r.key}`}
            className="rounded-xl border border-subtle p-3"
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
              {r.title}
            </div>
            <div className="mt-1.5">
              <StarRow stars={r.stars} />
            </div>
            {r.average !== "" && r.average != null && (
              <div className="mt-1.5 text-xs text-slate-600">
                {r.averageLabel ? `${r.averageLabel}: ` : ""}
                <span className="font-semibold text-slate-800">{r.average}</span>
                {r.averageUnit ? ` ${r.averageUnit}` : ""}
              </div>
            )}
            {r.description && (
              <div className="mt-1 text-xs text-slate-500 leading-snug">
                {r.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
